import { createContext, useContext, useEffect, type CSSProperties, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { CalendarClock, Clock3, Globe2 } from 'lucide-react'
import type { VenuePublic } from '@seaa/shared'
import { cn } from '../../lib/cn'
import { useI18n } from '../../lib/i18n'
import { useSiteContext } from '../../lib/site-context'
import { localizedProductName } from '../../lib/brand'
import { normalizeBrandColor, readableBrandInk } from '../../lib/brand-color'
import { usePageMetadata } from '../../lib/page-metadata'
import { useAppRuntime } from '../../lib/app-runtime'
import './booking-flow.css'

const BookingVenueContext = createContext<VenuePublic | null>(null)
const ALL_PUBLIC_LOCALES: NonNullable<VenuePublic['enabledLocales']> = ['ar', 'en', 'de', 'nl']

function publicLaunchLocales(locales: NonNullable<VenuePublic['enabledLocales']>) {
  const enabled = locales.filter((value) => ALL_PUBLIC_LOCALES.includes(value))
  return enabled.length > 0 ? enabled : ALL_PUBLIC_LOCALES
}

type BookingStyle = CSSProperties & {
  '--accent'?: string
  '--accent-ink'?: string
}

function venueInitials(name: string) {
  return name.trim().split(/\s+/).slice(0, 2).map((word) => Array.from(word)[0]).join('')
}

export function BookingExperience({ venue, className, children }: {
  venue?: VenuePublic | null | undefined
  className?: string | undefined
  children: ReactNode
}) {
  const { site } = useSiteContext()
  const runtime = useAppRuntime()
  const { applySiteLocale } = useI18n()
  const accent = normalizeBrandColor(site?.accentColor ?? venue?.accentColor)
  const accentInk = accent ? readableBrandInk(accent, site?.accentContrastColor ?? venue?.accentContrastColor) : null
  const brandSlug = site?.venueSlug ?? venue?.slug ?? 'seaa'
  const defaultLocale = site?.defaultLocale ?? venue?.defaultLocale ?? 'ar'
  const enabledLocales = publicLaunchLocales(site?.enabledLocales ?? venue?.enabledLocales ?? ALL_PUBLIC_LOCALES)
  const typographyPack = site?.typographyPack ?? venue?.typographyPack ?? 'active'
  const businessName = site?.publicTitle || venue?.publicTitle || site?.venueName || venue?.name || localizedProductName(defaultLocale)
  const description = site?.publicDescription || venue?.publicDescription || (defaultLocale === 'ar' ? `احجز لدى ${businessName} واطّلع على المواعيد المتاحة مباشرة.` : `Book ${businessName} and see live availability.`)
  const canonicalUrl = `${runtime.origin}/book/${brandSlug}`
  const imageSource = site?.coverUrl || venue?.imageUrl || site?.logoUrl || venue?.logoUrl
  const imageUrl = imageSource ? new URL(imageSource, runtime.origin).toString() : null
  usePageMetadata({
    enabled: Boolean(site || venue),
    title: defaultLocale === 'ar' ? `احجز لدى ${businessName} · سِعَة` : `Book ${businessName} · Seaa`,
    description,
    canonicalUrl,
    imageUrl,
    type: 'business.business',
    robots: runtime.pathname === `/book/${brandSlug}` ? 'index,follow,max-image-preview:large' : 'noindex,nofollow',
    alternates: enabledLocales.map((value) => ({ locale: value, url: `${canonicalUrl}?lang=${value}` })),
    structuredData: { '@context': 'https://schema.org', '@type': 'LocalBusiness', name: businessName, description, url: canonicalUrl, image: imageUrl || undefined, address: { '@type': 'PostalAddress', addressLocality: site?.city || venue?.city }, inLanguage: enabledLocales },
    revision: `${brandSlug}:${defaultLocale}:${runtime.pathname}`,
  })
  const style: BookingStyle = {
    ...(accent ? { '--accent': accent } : {}),
    ...(accentInk ? { '--accent-ink': accentInk } : {}),
  }

  useEffect(() => {
    applySiteLocale(defaultLocale, enabledLocales)
    const root = document.documentElement
    const previousTone = root.getAttribute('data-tone')
    const previousBooking = root.getAttribute('data-booking-active')
    const previousAccent = root.style.getPropertyValue('--accent')
    const previousAccentInk = root.style.getPropertyValue('--accent-ink')
    root.setAttribute('data-tone', 'active')
    root.setAttribute('data-booking-active', 'true')
    if (accent) root.style.setProperty('--accent', accent)
    if (accentInk) root.style.setProperty('--accent-ink', accentInk)
    return () => {
      if (previousTone) root.setAttribute('data-tone', previousTone)
      else root.removeAttribute('data-tone')
      if (previousBooking) root.setAttribute('data-booking-active', previousBooking)
      else root.removeAttribute('data-booking-active')
      if (previousAccent) root.style.setProperty('--accent', previousAccent)
      else root.style.removeProperty('--accent')
      if (previousAccentInk) root.style.setProperty('--accent-ink', previousAccentInk)
      else root.style.removeProperty('--accent-ink')
    }
  }, [accent, accentInk, applySiteLocale, defaultLocale, enabledLocales])

  return <BookingVenueContext.Provider value={venue ?? null}>
    <div className={cn('booking-active min-h-dvh', className)} data-tone="active" data-booking-brand={brandSlug} data-typography={typographyPack} style={style}>{children}</div>
  </BookingVenueContext.Provider>
}

export function BookingHeader({ venueSlug, context = 'booking' }: { venueSlug?: string | undefined; context?: 'booking' | 'manage' }) {
  const { locale, setLocale, l } = useI18n()
  const { site } = useSiteContext()
  const venue = useContext(BookingVenueContext)
  const enabledLocales = publicLaunchLocales(site?.enabledLocales ?? venue?.enabledLocales ?? ALL_PUBLIC_LOCALES)
  const brand = site?.publicTitle || venue?.publicTitle || site?.venueName || venue?.name || localizedProductName(locale)
  const logoUrl = site?.logoUrl ?? venue?.logoUrl
  const hideAttribution = site?.hideSanadBranding ?? venue?.hideSanadBranding ?? false
  const home = site ? `/?lang=${locale}` : venueSlug ? `/book/${venueSlug}?lang=${locale}` : undefined
  const languageLabel = l('Sprache wählen', 'Choose language', 'اختيار اللغة', 'Taal kiezen')
  const brandContent = <>
    {logoUrl ? <img src={logoUrl} alt="" className="booking-active__logo" /> : <span className="booking-active__monogram" aria-hidden="true">{venueInitials(brand)}</span>}
    <span className="booking-active__brand-copy"><strong dir="auto">{brand}</strong>{!hideAttribution && <small>{l('Buchung mit Seaa', 'Booking by Seaa', 'الحجز عبر سِعَة', 'Boeken met Seaa')}</small>}</span>
  </>

  return <header className="booking-active__header">
    <div className="booking-active__header-inner">
      {home ? <a href={home} className="booking-active__brand" aria-label={brand}>{brandContent}</a> : <span className="booking-active__brand">{brandContent}</span>}
      <div className="booking-active__utilities">
        <label className="booking-active__locale" title={languageLabel}>
          <Globe2 size={18} aria-hidden="true" />
          <span className="booking-active__locale-code" aria-hidden="true">{locale === 'ar' ? 'AR' : locale.toUpperCase()}</span>
          <span className="sr-only">{languageLabel}</span>
          <select value={locale} onChange={(event) => setLocale(event.target.value as typeof locale)} aria-label={languageLabel}>
            {enabledLocales.map((value) => <option key={value} value={value}>{value === 'ar' ? 'العربية' : value.toUpperCase()}</option>)}
          </select>
        </label>
        {context === 'booking' && <Link to="/manage" search={{ code: undefined, lang: locale }} className="booking-active__manage" aria-label={l('Meine Buchung', 'My booking', 'إدارة حجزي', 'Mijn boeking')}><CalendarClock size={18} /><span>{l('Meine Buchung', 'My booking', 'إدارة حجزي', 'Mijn boeking')}</span></Link>}
      </div>
    </div>
  </header>
}

export function VenueStrip({ name, city, imageUrl, opensAt, closesAt, publicTitle, publicDescription }: VenuePublic) {
  const { locale, l } = useI18n()
  const title = publicTitle || name
  return <section className="booking-active__venue">
    <div className="booking-active__venue-inner">
      <div className="booking-active__venue-copy">
        <p className="booking-active__venue-kicker">{l('Direkt beim Betrieb buchen', 'Book direct with the business', 'احجز مباشرة لدى المنشأة', 'Boek rechtstreeks bij het bedrijf')}</p>
        <h1 dir="auto">{title}</h1>
        {publicDescription && <p className="booking-active__venue-description" dir="auto">{publicDescription}</p>}
        <div className="booking-active__venue-meta"><span dir="auto">{city}</span><span><Clock3 size={17} /> {opensAt && closesAt
          ? <>{l('Öffnungszeiten', 'Opening hours', 'ساعات العمل', 'Openingstijden')} <bdi>{opensAt}–{closesAt}</bdi></>
          : l('An diesem Tag geschlossen', 'Closed on this date', 'مغلق في هذا التاريخ', 'Gesloten op deze datum')}</span></div>
      </div>
      {imageUrl && <figure className="booking-active__venue-media"><img src={imageUrl} alt={locale === 'de' ? `Foto von ${name}` : locale === 'nl' ? `Foto van ${name}` : locale === 'ar' ? `صورة ${name}` : `Photo of ${name}`} width="640" height="400" fetchPriority="high" /></figure>}
    </div>
  </section>
}

export function Steps({ current }: { current: 1 | 2 | 3 }) {
  const { t, l } = useI18n()
  const labels = [t('stepTime'), t('stepDetails'), t('stepPayment')]
  return <nav className="booking-active__steps" aria-label={l('Buchungsschritte', 'Booking steps', 'خطوات الحجز', 'Boekingsstappen')}>
    <ol>{labels.map((label, index) => {
      const step = index + 1
      return <li key={label} className={cn(current === step && 'is-current', current > step && 'is-complete')} aria-current={current === step ? 'step' : undefined}>
        <span>{step}</span><strong>{label}</strong>
      </li>
    })}</ol>
  </nav>
}
